import { useEffect, useState } from 'react';
import { ApiError, Button, Input } from '@dataroom/shared';
import { useCreateSupportRequest } from '@/api/hooks';
import { Modal } from './Modal';

interface ContactSupportModalProps {
  onClose: () => void;
  /** Pre-fills the subject field — e.g. "Reopen room: Project Atlas" from the
   * closed-room card. Empty when opened from the Help center's
   * "Contact support" button. */
  prefillSubject?: string;
  /** Free-text pointer to what prompted the request (e.g. a room id), sent as
   * `contextRef`. Not shown in the form — just carried through to the request. */
  prefillContextRef?: string;
}

/**
 * The one "Contact support" surface in the app (PR: help-support-surface).
 * Reuses {@link Modal}; owns its own mutation since it is opened from two
 * unrelated places (the Help center, empty, and the closed-room config card,
 * pre-filled) with nothing else to coordinate. On success it shows a brief
 * confirmation and closes itself — there is no request listing anywhere in
 * the app for the caller to refetch.
 */
export function ContactSupportModal({
  onClose,
  prefillSubject,
  prefillContextRef,
}: ContactSupportModalProps) {
  const [subject, setSubject] = useState(prefillSubject ?? '');
  const [body, setBody] = useState('');
  const create = useCreateSupportRequest();

  // Auto-close a couple seconds after the confirmation shows.
  useEffect(() => {
    if (!create.isSuccess) return;
    const t = setTimeout(onClose, 2000);
    return () => clearTimeout(t);
  }, [create.isSuccess, onClose]);

  const subjectTrimmed = subject.trim();
  const bodyTrimmed = body.trim();
  const canSubmit = !create.isPending && subjectTrimmed.length > 0 && bodyTrimmed.length > 0;

  const submit = () => {
    if (!canSubmit) return;
    create.mutate({
      subject: subjectTrimmed,
      body: bodyTrimmed,
      contextRef: prefillContextRef,
    });
  };

  const error = create.error instanceof ApiError ? create.error.message : create.error ? 'Could not send your request.' : null;

  if (create.isSuccess) {
    return (
      <Modal title="Contact support" onClose={onClose} footer={<Button size="sm" onClick={onClose}>Close</Button>}>
        <p className="text-sm">Thanks — we&apos;ll be in touch.</p>
      </Modal>
    );
  }

  return (
    <Modal
      title="Contact support"
      description="Send a message to the team running this data room."
      onClose={onClose}
      footer={
        <>
          {error ? <p className="text-sm text-destructive">{error}</p> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={create.isPending}>
              Cancel
            </Button>
            <Button size="sm" disabled={!canSubmit} onClick={submit}>
              {create.isPending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="support-subject" className="text-sm font-medium">
            Subject
          </label>
          <Input
            id="support-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={create.isPending}
            maxLength={200}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="support-body" className="text-sm font-medium">
            Message
          </label>
          <textarea
            id="support-body"
            className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={create.isPending}
            maxLength={5000}
          />
        </div>
      </div>
    </Modal>
  );
}
